import type { FC } from 'react';
import React, { useRef, useState, lazy, Suspense } from 'react';
import { Picture } from '@ui';
import { Link, useLocation } from '@compat/router';
import icons from '@icons';
import useWindowParamsOnScroll from '@customHooks/useWindowParamsOnScroll';

import CatalogBtn from './CatalogBtn';
import PromosBtn from './PromosBtn';
import HeaderSearch from './HeaderSearch';
import HeaderCollections from '@components/Carousel/HeaderCollections';

import RepeatBtn from './RepeatBtn';
import LikesBtn from './LikesBtn';
import AccountBtn from './AccountBtn';
import { observer } from 'mobx-react';
import HeaderLogo from './HeaderLogo';
import useWindowSize from '@customHooks/useWindowSize';
import HeaderCart from './HeaderCart';

// модалки рендерятся по условию ниже → грузим лениво (уносим из main-бандла;
// react-share, imask и пр. едут в чанки этих модалок). Suspense fallback=null —
// до открытия ничего не рисуется, поведение mount/unmount не меняется.
const MergeCartsModal = lazy(
  async () => await import('@components/Modals/MergeCartsModal')
);
const FlowersPostcardModal = lazy(
  async () => await import('@components/Modals/FlowersPostcardModal')
);
const CallbackModal = lazy(
  async () => await import('@components/Modals/CallbackModal')
);
import SuccessModalWithTimer from '@components/Modals/SuccessModalWithTimer';
const DigitalModal = lazy(
  async () => await import('@components/Modals/DigitalModal')
);
const ShareSocialsModal = lazy(
  async () => await import('@components/Modals/ShareSocialsModal')
);
import { twcls } from '@utils/twcls';
import BarBtn from './BarBtn';
import { useStores } from '@root/src/stores';
import './index.scss';

const {
  flash: { red: flashRedFilled }
} = icons.Fill;

interface MainHeaderProps {
  isOnlyMainHeader?: boolean;
}

const MainHeader: FC<MainHeaderProps> = observer(({ isOnlyMainHeader }) => {
  const { modalsStore, cartStore } = useStores();
  const {
    closeCallbackModal,
    isShowCallbackModal,
    // showLeftSiteModal,
    isShowDigitalModal,
    isShowShareSocials,
    closeShareSocials,
    showMergeModal
  } = modalsStore;
  const { isOpenPostCard } = cartStore;
  // const { isUserLoggedIn, isSubscribedToEmail, user } = userStore;
  const [isSuccessModalOpened, setIsSuccessModalOpened] = useState(false);
  const closeSuccessModal = (): void => {
    setIsSuccessModalOpened(false);
  };
  const changeToSuccessModal = (): void => {
    closeCallbackModal();
    setIsSuccessModalOpened(true);
  };
  // ТУТ
  const [isShowCatalogListDesktop, setIsShowCatalogListDesktop] =
    useState(false);

  const desktopCatalogBtnComponent = useRef<{
    hide;
  }>(null);

  const isShowHeaderShadow = useWindowParamsOnScroll(18);

  const { isMobile, isTablet, isDesktop } = useWindowSize();

  const { pathname } = useLocation();
  // страницы, где на мобилке коллекции не должны быть sticky
  const isStaticCollectionsPage =
    pathname.startsWith('/catalog') ||
    pathname.startsWith('/search') ||
    pathname.startsWith('/supplier/') ||
    pathname.startsWith('/product/');
  const shouldShowCollections =
    !isOnlyMainHeader &&
    !pathname.startsWith('/cart') &&
    !(isMobile && pathname.startsWith('/cabinet'));

  const hideLinks = (): void => {
    closeShareSocials();
  };

  return (
    <>
      {(isMobile || isTablet) && (
        <>
          <header className='header-main '>
            <div className='header-main__main'>
              {isMobile ? (
                <>
                  <HeaderLogo />
                  <HeaderSearch />
                  <CatalogBtn />
                </>
              ) : (
                <>
                  <div className='flex items-center gap-3 md:gap-6'>
                    <CatalogBtn />
                    <HeaderSearch />
                    <BarBtn />
                  </div>
                  <div className='absolute -translate-x-1/2 left-1/2'>
                    <HeaderLogo />
                  </div>
                  <div className='flex items-center gap-3 ml-auto md:gap-2'>
                    <LikesBtn />
                    <AccountBtn />
                    <HeaderCart />
                  </div>
                </>
              )}
            </div>
            {shouldShowCollections &&
              !(isMobile && isStaticCollectionsPage) && <HeaderCollections />}
          </header>
          {/* На /catalog, /search, /supplier/$id и /product/$id коллекции
              рендерятся вне sticky-шапки, чтобы уезжать при скролле */}
          {isMobile && isStaticCollectionsPage && shouldShowCollections && (
            <HeaderCollections isStatic />
          )}
        </>
      )}
      {isDesktop && (
        <header
          className={`header-main${
            isShowHeaderShadow &&
            // ЭТАП 1 · Шаг 3 — гидрация: pathname из useLocation (см. выше),
            // а не bare location (сервер/клиент читали разные источники)
            !pathname.includes('catalog') &&
            !pathname.includes('promotions')
              ? ' header-main--shadow'
              : ''
          }`}
        >
          <div className='header-main__main'>
            <HeaderLogo isShowHeaderShadow={isShowHeaderShadow} />
            <CatalogBtn
              isShow={isShowCatalogListDesktop}
              setIsShow={setIsShowCatalogListDesktop}
              ref={desktopCatalogBtnComponent}
            />
            <Link className='header-main__newbee' to={'/catalog/newbee'}>
              <Picture
                pictureClassName='header-main__newbee__icon'
                icon={flashRedFilled}
                width='20'
                height='20'
              />
              Новинки
            </Link>
            <PromosBtn />
            <HeaderSearch />
            <RepeatBtn />
            <LikesBtn />
            <AccountBtn />
            <HeaderCart />
          </div>
          {isShowCatalogListDesktop && (
            <div
              onMouseEnter={desktopCatalogBtnComponent.current?.hide}
              className={twcls(`
        fixed
        z-[1001]
        top-0
        left-0
        w-full
        h-full
        bg-black/5
        backdrop-blur-[2.5px]
        transition-opacity
        duration-300
      `)}
            />
          )}
          {shouldShowCollections && <HeaderCollections />}
        </header>
      )}
      {isShowDigitalModal ? (
        <Suspense fallback={null}>
          <DigitalModal />
        </Suspense>
      ) : null}
      {showMergeModal && (
        <Suspense fallback={null}>
          <MergeCartsModal />
        </Suspense>
      )}

      {isMobile && isShowShareSocials && (
        <Suspense fallback={null}>
          <ShareSocialsModal closeModal={hideLinks} />
        </Suspense>
      )}
      {isOpenPostCard && (
        <Suspense fallback={null}>
          <FlowersPostcardModal />
        </Suspense>
      )}
      {isShowCallbackModal && (
        <Suspense fallback={null}>
          <CallbackModal
            onClose={closeCallbackModal}
            changeToSuccessModal={changeToSuccessModal}
          />
        </Suspense>
      )}
      {isSuccessModalOpened && (
        <SuccessModalWithTimer
          closeModal={closeSuccessModal}
          title='Спасибо!'
          text='Мы перезвоним вам в самое ближайшее время'
        />
      )}
    </>
  );
});

export default MainHeader;
